import type { ComponentPropsWithRef } from 'react';

import { Button } from '@connected/ui/components/button';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';
import { Calendar as CalendarIcon } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { PrimativeFieldPropsOmit, PrimativeLabelType } from '../../lib/utils';
import type { DatePickerProps, DatePickerValueType } from '../form/date-picker';

import { useFieldContext } from '../../hooks/use-form-context';
import { DatePicker } from '../form/date-picker';

export type DatePickerFieldProps<T extends DatePickerValueType> = Omit<DatePickerProps<T>, PrimativeFieldPropsOmit> & {};

export function DatePickerField<T extends DatePickerValueType>(props: DatePickerFieldProps<T>) {
    const field = useFieldContext<T>();

    return (
        <DatePicker
            id={field.name}
            name={field.name}
            value={field.state.value}
            onChange={field.handleChange}
            onBlur={field.handleBlur}
            {...props}
        />
    );
}

export type DatePickerFieldTriggerProps = Omit<ComponentPropsWithRef<typeof Button>, 'children'> & {
    placeholder?: PrimativeLabelType;
};

export function DatePickerFieldTrigger(props: DatePickerFieldTriggerProps) {
    const field = useFieldContext<DatePickerValueType>();
    const { t } = useTranslation('form-fields');

    const displayDate = useMemo(() => field.state.value
        ? format(
                typeof field.state.value === 'string'
                    ? new Date(field.state.value)
                    : field.state.value,
                'PPP',
                { locale: nl },
            )
        : props.placeholder || t('date-picker.placeholder'), [field.state.value, props.placeholder, t]);

    return (
        <Button
            variant="input"
            {...props}
        >
            <span className="w-full text-left">{displayDate}</span>
            <CalendarIcon size={20} />
        </Button>
    );
}

DatePickerField.Trigger = DatePickerFieldTrigger;
DatePickerFieldTrigger.displayName = 'DatePickerFieldTrigger';
